import axios from 'axios';

const authConfig = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
});

export const listExternalAppProfiles = async (deviceId, { cacheBust = false } = {}) => {
  const response = await axios.get('/api/external-apps/profiles', {
    ...authConfig(),
    params: { deviceId, ...(cacheBust ? { _cacheBust: Date.now() } : {}) },
  });
  return response.data?.profiles || [];
};

export const listExternalAppAdapters = async () => {
  const response = await axios.get('/api/external-apps/adapters', authConfig());
  return response.data?.adapters || [];
};

export const resolveExternalAppAdapter = async (packageName, adapterKey = null) => {
  const response = await axios.get('/api/external-apps/adapters/resolve', {
    ...authConfig(),
    params: { packageName, ...(adapterKey ? { adapterKey } : {}) },
  });
  return response.data?.adapter || null;
};

export const saveExternalAppProfile = async (deviceId, packageName, payload) => {
  const response = await axios.put(
    `/api/external-apps/profiles/${encodeURIComponent(packageName)}`,
    { deviceId, ...payload },
    authConfig()
  );
  return response.data?.profile || null;
};

export const startExternalAppCalibration = async (profileId) => {
  const response = await axios.post(
    `/api/external-apps/profiles/${profileId}/calibration-sessions`,
    {},
    authConfig()
  );
  return response.data?.session || null;
};

export const getExternalAppCalibration = async (sessionId) => {
  const response = await axios.get(
    `/api/external-apps/calibration-sessions/${sessionId}`,
    authConfig()
  );
  return response.data?.session || null;
};

export const captureExternalAppEvidence = async (
  sessionId,
  includeScreenshot = false,
  waitForForegroundMs = 0
) => {
  const response = await axios.post(
    `/api/external-apps/calibration-sessions/${sessionId}/evidence`,
    { includeScreenshot, waitForForegroundMs },
    authConfig()
  );
  return response.data?.evidence || null;
};

export const testExternalAppAction = async (sessionId, action, includeScreenshot = false) => {
  const response = await axios.post(
    `/api/external-apps/calibration-sessions/${sessionId}/tests`,
    { action, includeScreenshot },
    authConfig()
  );
  return response.data?.test || null;
};

export const sendExternalAppCalibrationAction = async (
  sessionId,
  action,
  transport = 'accessibility'
) => {
  const response = await axios.post(
    `/api/external-apps/calibration-sessions/${sessionId}/actions`,
    { action, transport },
    authConfig()
  );
  return response.data || null;
};

export const sendExternalAppSetupRemoteAction = async (deviceId, action) => {
  const response = await axios.post(
    `/api/site-control/targets/${deviceId}/action`,
    { action },
    authConfig()
  );
  return response.data || null;
};

export const resetExternalAppCalibrationAction = async (sessionId, action) => {
  const response = await axios.delete(
    `/api/external-apps/calibration-sessions/${sessionId}/actions/${encodeURIComponent(action)}`,
    authConfig()
  );
  return response.data || null;
};

export const saveExternalAppCalibrationStartPoint = async (sessionId, startPoint) => {
  const response = await axios.post(
    `/api/external-apps/calibration-sessions/${sessionId}/start-points`,
    startPoint,
    authConfig()
  );
  return response.data?.startPoint || null;
};

export const updateExternalAppCalibrationStartPoint = async (
  sessionId,
  startPointId,
  patch
) => {
  const response = await axios.put(
    `/api/external-apps/calibration-sessions/${sessionId}/start-points/${encodeURIComponent(startPointId)}`,
    patch,
    authConfig()
  );
  return response.data?.startPoint || null;
};

export const testExternalAppCalibrationStartPoint = async (sessionId, startPointId) => {
  const response = await axios.post(
    `/api/external-apps/calibration-sessions/${sessionId}/start-points/${encodeURIComponent(startPointId)}/test`,
    {},
    authConfig()
  );
  return response.data?.result || null;
};

export const listExternalAppContentTargets = async (deviceId, { cacheBust = false } = {}) => {
  const response = await axios.get('/api/external-apps/content-targets', {
    ...authConfig(),
    params: { deviceId, ...(cacheBust ? { _cacheBust: Date.now() } : {}) },
  });
  return response.data?.targets || [];
};

export const createExternalAppContentTarget = async (payload) => {
  const response = await axios.post(
    '/api/external-apps/content-targets',
    payload,
    authConfig()
  );
  return response.data?.target || null;
};

export const updateExternalAppContentTarget = async (targetId, payload) => {
  const response = await axios.put(
    `/api/external-apps/content-targets/${targetId}`,
    payload,
    authConfig()
  );
  return response.data?.target || null;
};

export const executeExternalAppContentTarget = async (targetId) => {
  const response = await axios.post(
    `/api/external-apps/content-targets/${targetId}/execute`,
    { source: 'calibration' },
    authConfig()
  );
  return response.data || null;
};

export const saveYouTubeExternalAppTarget = async (profileId, payload) => {
  const response = await axios.post(
    `/api/external-apps/profiles/${profileId}/youtube-targets`,
    payload,
    authConfig()
  );
  return response.data || null;
};

export const playExternalAppSeed = async (profileId, payload) => {
  const response = await axios.post(
    `/api/external-apps/profiles/${profileId}/seed-executions`,
    payload,
    authConfig()
  );
  return response.data || null;
};

export const previewExternalMediaLink = async (url) => {
  const response = await axios.post(
    '/api/external-apps/external-media-links/preview',
    { url },
    authConfig()
  );
  return response.data?.media || null;
};

export const searchProviderContent = async (provider, query) => {
  const response = await axios.get('/api/external-apps/provider-content-search', {
    ...authConfig(),
    params: { provider, q: query },
  });
  return response.data || null;
};

export const searchIptvCatalog = async ({
  query = '',
  country = '',
  category = '',
  page = 1,
  limit = 40,
} = {}) => {
  const response = await axios.get('/api/external-apps/iptv-catalog', {
    ...authConfig(),
    timeout: 45000,
    params: {
      q: query,
      country,
      category,
      page,
      limit,
    },
  });
  return response.data || null;
};

export const saveExternalMediaLinkTarget = async (profileId, payload) => {
  const response = await axios.post(
    `/api/external-apps/profiles/${profileId}/link-targets`,
    payload,
    authConfig()
  );
  return response.data || null;
};

export const saveExternalMediaLinkToArea = async (profileId, payload) => {
  const response = await axios.post(
    `/api/external-apps/profiles/${profileId}/link-targets/area`,
    payload,
    authConfig()
  );
  return response.data || null;
};

export const confirmExternalAppTest = async (sessionId, testId, outcome) => {
  const response = await axios.post(
    `/api/external-apps/calibration-sessions/${sessionId}/tests/${testId}/confirmation`,
    { outcome },
    authConfig()
  );
  return response.data?.test || null;
};

export const recordExternalAppBaseline = async (sessionId, includeScreenshot = false) => {
  const response = await axios.post(
    `/api/external-apps/calibration-sessions/${sessionId}/baselines`,
    { name: 'playback', includeScreenshot, confirmedByUser: true },
    authConfig()
  );
  return response.data?.baseline || null;
};

export const completeExternalAppCalibration = async (sessionId) => {
  const response = await axios.post(
    `/api/external-apps/calibration-sessions/${sessionId}/complete`,
    {},
    authConfig()
  );
  return response.data;
};

export const saveExternalAppLaunchRecipe = async (sessionId, launchRecipe) => {
  const response = await axios.put(
    `/api/external-apps/calibration-sessions/${sessionId}/launch-recipe`,
    launchRecipe,
    authConfig()
  );
  return response.data?.launchRecipe || null;
};

export const getExternalAppPlaybook = async (sessionId) => {
  const response = await axios.get(
    `/api/external-apps/calibration-sessions/${sessionId}/playbook`,
    authConfig()
  );
  return response.data?.playbook || null;
};

export const saveExternalAppPlaybook = async (sessionId, playbook) => {
  const response = await axios.put(
    `/api/external-apps/calibration-sessions/${sessionId}/playbook`,
    playbook,
    authConfig()
  );
  return response.data?.playbook || null;
};

export const executeExternalAppDestination = async (
  profileId,
  destinationId,
  { source = 'calibration', timeoutMs = 180000 } = {}
) => {
  const response = await axios.post(
    `/api/external-apps/profiles/${profileId}/destinations/${encodeURIComponent(destinationId)}/execute`,
    {
      source,
      timeoutMs,
      operationKey: `app-destination-${profileId}-${destinationId}-${Date.now()}`,
    },
    authConfig()
  );
  return response.data || null;
};

export const getExternalAppSourceOperation = async (operationId) => {
  const response = await axios.get(
    `/api/external-apps/source-operations/${operationId}`,
    authConfig()
  );
  return response.data || null;
};

export const listExternalAppPlaybookCopyTargets = async (profileId) => {
  const response = await axios.get(
    `/api/external-apps/profiles/${profileId}/playbook-copy-targets`,
    authConfig()
  );
  return response.data?.targets || [];
};

export const copyExternalAppPlaybook = async (profileId, deviceIds) => {
  const response = await axios.post(
    `/api/external-apps/profiles/${profileId}/playbook-copy`,
    { deviceIds },
    authConfig()
  );
  return response.data || null;
};

export const getExternalAppListGeometry = async (sessionId) => {
  const response = await axios.get(
    `/api/external-apps/calibration-sessions/${sessionId}/list-geometry`,
    authConfig()
  );
  return response.data?.listGeometry || null;
};

export const trainExternalAppListGeometry = async (sessionId, payload) => {
  const response = await axios.put(
    `/api/external-apps/calibration-sessions/${sessionId}/list-geometry`,
    payload,
    authConfig()
  );
  return response.data || null;
};

export const testExternalAppListGeometry = async (sessionId, payload) => {
  const response = await axios.post(
    `/api/external-apps/calibration-sessions/${sessionId}/list-geometry/test`,
    payload,
    authConfig()
  );
  return response.data?.result || null;
};

export const listExternalAppMacros = async (packageName) => {
  try {
    const response = await axios.get('/api/remote/macros', { params: { packageName } });
    return response.data?.macros || {};
  } catch (error) {
    const response = await axios.get('/controllers/api/remote/macros', {
      params: { packageName },
    });
    return response.data?.macros || {};
  }
};

export const runExternalAppMacro = async ({ deviceId, packageName, macroName, macro }) => {
  const payload = {
    deviceId,
    packageName,
    macroName,
    method: macro?.method,
    steps: macro?.steps || [],
  };
  try {
    return (await axios.post('/api/remote/macro', payload)).data;
  } catch (error) {
    return (await axios.post('/controllers/api/remote/macro', payload)).data;
  }
};

export const abandonExternalAppCalibration = async (sessionId) => {
  const response = await axios.post(
    `/api/external-apps/calibration-sessions/${sessionId}/abandon`,
    {},
    authConfig()
  );
  return response.data;
};

export const getExternalAppEvidenceScreenshot = async (sessionId, evidenceId) => {
  const response = await axios.get(
    `/api/external-apps/calibration-sessions/${sessionId}/evidence/${evidenceId}/screenshot`,
    authConfig()
  );
  return response.data?.screenshot || null;
};

export const saveExternalAppPromptRule = async (sessionId, promptRule) => {
  const response = await axios.post(
    `/api/external-apps/calibration-sessions/${sessionId}/prompt-rules`,
    promptRule,
    authConfig()
  );
  return response.data?.promptRule || null;
};

export const updateExternalAppPromptRule = async (sessionId, ruleId, promptRule) => {
  const response = await axios.put(
    `/api/external-apps/calibration-sessions/${sessionId}/prompt-rules/${encodeURIComponent(ruleId)}`,
    promptRule,
    authConfig()
  );
  return response.data?.promptRule || null;
};

export const deleteExternalAppPromptRule = async (sessionId, ruleId) => {
  const response = await axios.delete(
    `/api/external-apps/calibration-sessions/${sessionId}/prompt-rules/${encodeURIComponent(ruleId)}`,
    authConfig()
  );
  return response.data || null;
};

export const testExternalAppPromptRuleDetection = async (sessionId, ruleId) => {
  const response = await axios.post(
    `/api/external-apps/calibration-sessions/${sessionId}/prompt-rules/${encodeURIComponent(ruleId)}/test-detection`,
    {},
    authConfig()
  );
  return response.data?.result || null;
};

export const testExternalAppPromptRuleResponse = async (sessionId, ruleId) => {
  const response = await axios.post(
    `/api/external-apps/calibration-sessions/${sessionId}/prompt-rules/${encodeURIComponent(ruleId)}/test-response`,
    {},
    authConfig()
  );
  return response.data?.result || null;
};

export const startExternalAppPromptRun = async (sessionId, trigger = 'manual') => {
  const response = await axios.post(
    `/api/external-apps/calibration-sessions/${sessionId}/prompt-runs`,
    {
      trigger,
      idempotencyKey: `prompt-${sessionId}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    },
    authConfig()
  );
  return response.data?.run || null;
};

export const launchExternalAppProfile = async (profileId) => {
  const response = await axios.post(
    `/api/external-apps/profiles/${profileId}/launch`,
    {},
    authConfig()
  );
  return response.data?.execution || null;
};

export const getExternalAppPromptRun = async (runId) => {
  const response = await axios.get(
    `/api/external-apps/prompt-runs/${runId}`,
    authConfig()
  );
  return response.data?.run || null;
};

export const getLatestExternalAppPromptRun = async (sessionId, scope = 'prompt') => {
  const response = await axios.get(
    `/api/external-apps/calibration-sessions/${sessionId}/prompt-runs/latest`,
    {
      ...authConfig(),
      params: { scope },
    }
  );
  return response.data?.run || null;
};

export const saveExternalAppPlaybackHealth = async (sessionId, config) => {
  const response = await axios.put(
    `/api/external-apps/calibration-sessions/${sessionId}/playback-health`,
    config,
    authConfig()
  );
  return response.data?.playbackHealth || null;
};

export const cancelExternalAppPromptRun = async (runId) => {
  const response = await axios.post(
    `/api/external-apps/prompt-runs/${runId}/cancel`,
    {},
    authConfig()
  );
  return response.data || null;
};

export const saveEnabledExternalApps = async (deviceId, enabledPackages) => {
  const response = await axios.post(
    '/api/devices/set-enabled-apps',
    { deviceId, enabledPackages },
    authConfig()
  );
  return response.data?.enabledPackages || [];
};
